import { useQuery } from "@apollo/client";
import { LoadingOverlay, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { GET_RETAILERS } from "apollo/queries";
import { useEffect, useState } from "react";

const PHONE_REGEX = /^09\d{8}$/;

export default function RetailerFilter({ onCardClick, required }) {
  const [phone, setPhone] = useState("");
  const [retailerName, setRetailerName] = useState("");
  const [error, setError] = useState(null);

  const [debouncedPhone] = useDebouncedValue(phone, 400);
  const isValidPhone = PHONE_REGEX.test(phone);

  // 🔥 Remove leading 0 before sending to backend
  const backendPhone = isValidPhone ? phone.slice(1) : "";

  const { data, loading } = useQuery(GET_RETAILERS, {
    variables: {
      first: 1,
      page: 1,
      search: backendPhone, // ✅ send without leading 0
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
    skip: !isValidPhone,
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: error?.graphQLErrors?.[0]?.message || "Failed to get retailer",
      });
    },
  });

  useEffect(() => {
    if (!isValidPhone) {
      setRetailerName("");
      onCardClick(null);
      return;
    }

    const retailer = data?.retailers?.data?.[0];

    if (retailer) {
      setRetailerName(retailer.name);
      onCardClick(retailer.id);
    } else {
      setRetailerName("");
      onCardClick(null);
    }
  }, [data, isValidPhone]);

  const handlePhoneChange = (e) => {
    const value = e.currentTarget.value.replace(/\D/g, "");
    setPhone(value);

    if (value && !PHONE_REGEX.test(value)) {
      setError("Invalid phone number");
    } else {
      setError(null);
    }
  };

  return (
    <>
      {loading && <LoadingOverlay visible />}

      <TextInput
        label="Retailer phone number"
        value={phone}
        onChange={handlePhoneChange}
        error={error}
        required={required}
        maxLength={10}
      />

      {retailerName && (
        <TextInput
          mt="sm"
          label="Retailer name"
          value={retailerName}
          readOnly
        />
      )}
    </>
  );
}
